// Admin helpers: conversions between the HostConfig wire shape and the form's
// editable representation (e.g. weekly hours as HH:MM strings, a single working
// window per day in the editor for simplicity). Implements: architecture.md
// v1.0 section 2 (HostConfig) + admin config editor.

import type { DayRange, HostConfig, WeeklyHours } from '../api/types.js';

export const DAY_NAMES: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

/** Minutes-from-midnight (0..1440) to 'HH:MM'. */
export function minutesToHHMM(min: number): string {
  const clamped = Math.max(0, Math.min(1440, Math.round(min)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 'HH:MM' to minutes-from-midnight; returns null on malformed input. */
export function hhmmToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 24 || m < 0 || m > 59) return null;
  const total = h * 60 + m;
  return total > 1440 ? null : total;
}

/** The editor models each weekday as enabled + a single [start,end] window. */
export interface DayForm {
  enabled: boolean;
  start: string; // HH:MM
  end: string; // HH:MM
}

export type WeekForm = Record<number, DayForm>;

const DEFAULT_DAY: DayForm = { enabled: false, start: '09:00', end: '17:00' };

/** Build the editable week form from a HostConfig's WeeklyHours. */
export function weekToForm(hours: WeeklyHours): WeekForm {
  const form: WeekForm = {};
  for (let d = 0; d <= 6; d++) {
    const ranges = hours[d as 0 | 1 | 2 | 3 | 4 | 5 | 6] ?? [];
    const first = ranges[0];
    form[d] = first
      ? { enabled: true, start: minutesToHHMM(first.startMin), end: minutesToHHMM(first.endMin) }
      : { ...DEFAULT_DAY };
  }
  return form;
}

/** Convert the editable week form back to WeeklyHours (validating times). */
export function formToWeek(form: WeekForm): { hours: WeeklyHours; error?: string } {
  const hours = {} as WeeklyHours;
  for (let d = 0; d <= 6; d++) {
    const day = form[d] ?? DEFAULT_DAY;
    const key = d as 0 | 1 | 2 | 3 | 4 | 5 | 6;
    if (!day.enabled) {
      hours[key] = [];
      continue;
    }
    const start = hhmmToMinutes(day.start);
    const end = hhmmToMinutes(day.end);
    if (start === null || end === null) {
      return { hours, error: `${DAY_NAMES[d]}: enter valid times (HH:MM).` };
    }
    if (end <= start) {
      return { hours, error: `${DAY_NAMES[d]}: end time must be after start time.` };
    }
    const range: DayRange = { startMin: start, endMin: end };
    hours[key] = [range];
  }
  return { hours };
}

/**
 * Sanitize an arbitrary handle into a URL-safe slug: lowercase, ASCII
 * letters/digits/hyphens only, internal runs of any other character collapsed to
 * a single hyphen, leading/trailing hyphens trimmed. Returns '' when nothing
 * usable remains. This is a local mirror of core's sanitizeSlug (provisioning.ts)
 * with identical behaviour; we replicate it here so the web package stays
 * dependency-free of core's built runtime output (web imports only core types).
 *
 * Used by the admin slug field so the input only ever holds URL-safe characters.
 * Examples:
 *   'My New Handle' -> 'my-new-handle'
 *   'a..b'          -> 'a-b'
 *   '---'           -> ''
 */
export function sanitizeSlug(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // any non-alphanumeric run -> single hyphen
    .replace(/^-+|-+$/g, ''); // trim leading/trailing hyphens
}

/**
 * First path segments reserved by the app router / platform paths; a private-event
 * slug can never be one of them (it would shadow /admin, the /api surface, the
 * /assets bundle, or the /manage path segment). Mirrors the server RESERVED_SLUGS
 * (packages/server/src/schemas.ts) and web/src/lib/router.ts, replicated here so
 * the admin lib stays free of the react-importing router module.
 */
export const RESERVED_SLUGS = ['admin', 'api', 'assets', 'manage'] as const;

/** True when a slug is one of the reserved first-segment words. */
export function isReservedSlug(slug: string): boolean {
  return (RESERVED_SLUGS as readonly string[]).includes(slug);
}

/** The URL-safe slug shape (mirror of core's sanitizeSlug output / server slugPattern). */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Live-validate a private-event slug (ADR-0003 section 7, risk 6) against the same
 * three rules the server enforces on save: non-empty + URL-safe, not a reserved
 * word, and unique among the host's OTHER event slugs. Returns a human-readable
 * message on failure, or null when valid. `otherSlugs` is every OTHER event slug
 * already in use on this host (never the slug being validated). This is a UX guard
 * only; the server schema is the source of truth.
 */
export function validateEventSlug(slug: string, otherSlugs: readonly string[]): string | null {
  if (!slug) return 'Enter a slug for the private event link.';
  if (!SLUG_PATTERN.test(slug)) return 'Use lowercase letters, numbers, and hyphens only.';
  if (isReservedSlug(slug)) return `"${slug}" is a reserved word and cannot be used.`;
  if (otherSlugs.includes(slug)) return `"${slug}" is already used by another event.`;
  return null;
}

let bookingTypeSeq = 0;
/** A stable-ish id for a newly added booking type in the editor. */
export function newBookingTypeId(): string {
  bookingTypeSeq += 1;
  return `bt_${Date.now().toString(36)}_${bookingTypeSeq}`;
}

/** Deep-ish clone of a HostConfig for safe local editing. */
export function cloneConfig(config: HostConfig): HostConfig {
  return JSON.parse(JSON.stringify(config)) as HostConfig;
}
